import { AuthContext } from '@/contexts/authContext';
import { AuthMode } from '@/enums';
import { useGetSubdomainQuery } from '@/hooks';
import {
  CSRF_HEADER,
  SSO_SESSION_AUTH,
} from '@/services/amili-v3-services/sso-session';
import {
  CustomButton,
  CustomModal,
  CustomSpin,
  Icon,
} from '@amili-v3/amili-v3-design';
import axios from 'axios';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const TENANT_SHAPE = /^[a-z0-9-]+$/i;

// auth-service redirects back here as `#token=<jwt>[&provisioned=true]` —
// a URL fragment, never sent to any server. Parsed by hand rather than
// useParseQueryString, which only reads the `?search` part of the URL.
const parseCallback = ({ hash, search }: Location) => ({
  token: new URLSearchParams(hash.replace(/^#/, '')).get('token') ?? undefined,
  tenant: new URLSearchParams(search).get('tenant') ?? undefined,
});

// On the bare host (invite links) the tenant's own pages are one subdomain
// label away; on a tenant's host they are right here.
const getTenantSignInUrl = (tenant?: string) => {
  const { protocol, host } = window.location;

  return tenant && TENANT_SHAPE.test(tenant) && !host.startsWith(`${tenant}.`)
    ? `${protocol}//${tenant}.${host}/sign-in`
    : '/sign-in';
};

/**
 * Trades the SSO token for the tenant's LILAC session cookie — the token
 * itself never reaches localStorage. See amili-api
 * docs/auth-migration-plan.md §10.3. Two ways in:
 * - sign-in from a tenant's dashboard returns to that tenant's own host;
 * - invite links return to the bare host with ?tenant=<subdomain> (the
 *   invite allowlist has no wildcards), which forwards to the tenant.
 */
const SSOCallback = () => {
  const { t } = useTranslation();
  const { setAuth } = useContext(AuthContext);
  const [failed, setFailed] = useState(false);
  const handled = useRef(false);
  const { isLoading, tenant: currentTenant } = useGetSubdomainQuery();
  const tenant =
    parseCallback(window.location).tenant ??
    (currentTenant?.authMode === AuthMode.SSO
      ? currentTenant.subdomain
      : undefined);

  useEffect(() => {
    if (handled.current || isLoading) return;
    handled.current = true;

    const { token } = parseCallback(window.location);

    // Drop the token from the address bar and history straight away.
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search,
    );

    if (!token || !tenant) {
      setFailed(true);
      return;
    }

    // Plain axios: APIService would attach this host's own (AMILI tenant)
    // bearer token.
    axios
      .post<{ redirectTo: string }>(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/session`,
        { token, tenant },
        { withCredentials: true, headers: { [CSRF_HEADER]: '1' } },
      )
      .then(({ data }) => {
        // Already on the tenant's host: mark it signed in so the app opens
        // directly instead of re-checking the session on the sign-in page.
        if (new URL(data.redirectTo).origin === window.location.origin) {
          setAuth(SSO_SESSION_AUTH);
        }

        window.location.replace(data.redirectTo);
      })
      .catch(() => setFailed(true));
  }, [isLoading, setAuth, tenant]);

  return (
    <CustomModal
      centered
      open
      mask={false}
      hideCloseButton
      footer={[]}
      title={<Icon name={'logoLilac'} width={107} height={36} />}
    >
      {failed ? (
        <>
          <p>{t('authentication.ssoLoginFailed')}</p>
          <CustomButton
            size="large"
            width="100%"
            onClick={() => window.location.replace(getTenantSignInUrl(tenant))}
          >
            {t('authentication.backToSignIn')}
          </CustomButton>
        </>
      ) : (
        <CustomSpin spinning>
          <div style={{ minHeight: 120 }} />
        </CustomSpin>
      )}
    </CustomModal>
  );
};

export default SSOCallback;
